import React, { useState } from "react";
import { Link } from "wouter";
import {
  useListAgents,
  useCreateAgent,
  useGetMe,
  getListAgentsQueryKey,
} from "@workspace/api-client-react";
import type { AgentType } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { TablePagination } from "@/components/TablePagination";
import { Plus, Loader2, ChevronRight } from "lucide-react";

const AGENT_TYPES: { value: AgentType; label: string }[] = [
  { value: "independent_technician", label: "Independent technician" },
  { value: "field_sales", label: "Field sales" },
  { value: "digital_partner", label: "Digital partner" },
];

const PAGE_SIZE = 10;

export function AgentsList() {
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useListAgents({ page, pageSize: PAGE_SIZE });
  const { data: me } = useGetMe();
  const [open, setOpen] = useState(false);

  if (isLoading) return <div className="p-6 animate-pulse"><div className="h-8 bg-gray-200 w-1/4 rounded mb-6"></div><div className="h-64 bg-gray-200 rounded"></div></div>;
  if (error || !data) return <div className="p-6 text-red-500">Failed to load agents.</div>;

  const agents = data.items;
  // Creating an agent requires entering the OpenServe code, which only admin /
  // account_manager may set. master_dealer (and read-only operations / poc_support)
  // are blocked from the code, so they cannot create agents — mirrors the server
  // POST /agents in-handler gate (admin/AM only).
  const canCreate = me?.role === "admin" || me?.role === "account_manager";

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-[#0B132B]">Agents</h1>
        {canCreate ? (
          <Button
            onClick={() => setOpen(true)}
            className="bg-[#E6007E] hover:bg-[#c4006b] text-white"
          >
            <Plus className="w-4 h-4 mr-2" /> Add agent
          </Button>
        ) : null}
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg text-[#0B132B]">All agents</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader className="bg-gray-50">
              <TableRow>
                <TableHead>Agent</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agents.map((agent) => (
                <TableRow key={agent.id} className="cursor-pointer hover:bg-gray-50">
                  <TableCell>
                    <Link href={`/agents/${agent.id}`} className="flex items-center gap-3">
                      <Avatar className="h-9 w-9 border border-gray-100">
                        <AvatarFallback className="bg-gray-100 text-[#0B132B] text-xs font-semibold">
                          {agent.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-[#0B132B]">{agent.name}</p>
                        <p className="text-xs text-gray-500">{agent.email || '-'}</p>
                      </div>
                    </Link>
                  </TableCell>
                  <TableCell className="capitalize">{agent.type.replace(/_/g, ' ')}</TableCell>
                  <TableCell className="font-medium">{agent.openserveDealerCode ?? agent.dealerCode ?? "—"}</TableCell>
                  <TableCell><StatusBadge status={agent.status} /></TableCell>
                  <TableCell>
                    <Link href={`/agents/${agent.id}`} className="text-gray-400 hover:text-[#E6007E] inline-flex">
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
              {agents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center p-8 text-gray-500">No agents found.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            page={page}
            pageSize={PAGE_SIZE}
            total={data.total}
            onPageChange={setPage}
          />
        </CardContent>
      </Card>

      {canCreate ? (
        <AddAgentDialog open={open} onOpenChange={setOpen} />
      ) : null}
    </div>
  );
}

function AddAgentDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [name, setName] = useState("");
  const [openserveCode, setOpenserveCode] = useState("");
  const [confirmCode, setConfirmCode] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [type, setType] = useState<AgentType>("field_sales");
  const createAgent = useCreateAgent();

  const trimmedCode = openserveCode.trim();
  // Double-entry: the OpenServe code is the agent's ONE code (never generated), so
  // it must be typed identically twice before the agent can be created.
  const codeFilled = trimmedCode.length > 0;
  const codesMatch = codeFilled && trimmedCode === confirmCode.trim();
  const showMismatch =
    confirmCode.trim().length > 0 && trimmedCode !== confirmCode.trim();

  const reset = () => {
    setName("");
    setOpenserveCode("");
    setConfirmCode("");
    setEmail("");
    setPhone("");
    setType("field_sales");
  };

  const submit = async () => {
    if (!name.trim() || !codesMatch) return;
    try {
      await createAgent.mutateAsync({
        data: {
          name: name.trim(),
          openserveDealerCode: trimmedCode,
          type,
          ...(email.trim() ? { email: email.trim() } : {}),
          ...(phone.trim() ? { phone: phone.trim() } : {}),
        },
      });
      queryClient.invalidateQueries({ queryKey: getListAgentsQueryKey() });
      toast({
        title: "Agent added",
        description: `${name.trim()} has been created.`,
      });
      reset();
      onOpenChange(false);
    } catch {
      toast({
        title: "Could not add agent",
        description: "Please check the details and try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[460px] rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-[#0B132B]">Add agent</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Full name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Thabo Mokoena" />
          </div>
          <div className="space-y-1.5">
            <Label>OpenServe dealer code</Label>
            <Input
              value={openserveCode}
              onChange={(e) => setOpenserveCode(e.target.value)}
              placeholder="Copied from the OpenServe portal"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Confirm OpenServe dealer code</Label>
            <Input
              value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value)}
              placeholder="Re-enter the code to confirm"
            />
            {showMismatch ? (
              <p className="text-xs text-red-600">The codes do not match.</p>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label>Agent type</Label>
            <Select value={type} onValueChange={(v) => setType(v as AgentType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AGENT_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Email (optional)</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="name@example.co.za" />
          </div>
          <div className="space-y-1.5">
            <Label>Mobile number (optional)</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" placeholder="082 000 0000" />
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={submit}
            disabled={!name.trim() || !codesMatch || createAgent.isPending}
            className="bg-[#E6007E] hover:bg-[#c4006b] text-white rounded-xl w-full"
          >
            {createAgent.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add agent"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
